'use client';

import clsx from 'clsx';
import { isEmpty } from 'lodash-es';
import {
  ChangeEvent,
  InputHTMLAttributes,
  JSX,
  ReactNode,
  Ref,
  WheelEvent,
  useRef,
  useState,
} from 'react';
import { ElementProps } from '../element';
import { useCombinedForwardedRef } from '../hooks/useCombinedForwardedRef';
import { IIconProps } from '../icon';
import * as defaultStyles from './input.css';

export interface InputIcon extends Pick<IIconProps, 'name' | 'size'> {
  onClick?: () => void;
}

export interface InputStyles {
  inputWrapper: (params: { disabled: boolean; error: boolean }) => string;
  input: (params: { disabled: boolean; error: boolean }) => string;
}

export interface InputProps
  extends Omit<
    InputHTMLAttributes<HTMLInputElement>,
    'onChange' | 'defaultValue' | 'value' | 'prefix'
  > {
  error?: boolean;
  initialValue?: string;
  value?: string;
  suffix?: (color: ElementProps['color']) => ReactNode;
  prefix?: (color: ElementProps['color']) => ReactNode;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  styles: InputStyles;
  ignorePattern?: string;
  as?: keyof JSX.IntrinsicElements;
  hideStepper?: boolean;
  ref?: Ref<HTMLInputElement>;
  disableScrollChange?: boolean;
  onWheel?: (e: WheelEvent<HTMLInputElement>) => void;
}

export const Input = ({
  className,
  onChange: controlledOnChange,
  value: controlledValue,
  initialValue = '',
  error = false,
  disabled = false,
  styles,
  suffix,
  prefix,
  hideStepper = false,
  type,
  disableScrollChange = type === 'number',
  ref,
  onWheel,
  ignorePattern,
  ...props
}: InputProps) => {
  type InputEventWithComposition = Event & { isComposing?: boolean };
  const inputRef = useCombinedForwardedRef<HTMLInputElement>(ref);
  const isComposingRef = useRef(false);
  const [isComposing, setIsComposing] = useState(false);
  const [composingValue, setComposingValue] = useState<string | null>(null);
  const [innerValue, setInnerValue] = useState(initialValue);

  const unControlledOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInnerValue(e.target.value);
  };

  const value = controlledValue ?? innerValue;
  const onChange = controlledOnChange ?? unControlledOnChange;

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const nativeEvent = e.nativeEvent as InputEventWithComposition;
    const { value } = e.target;
    // During composition, update composing buffer and notify parent without sanitizing.
    if (isComposingRef.current || nativeEvent.isComposing) {
      setIsComposing(true);
      setComposingValue(value);
      if (controlledValue === undefined) {
        setInnerValue(value);
      }
      // Propagate to parent so controlled value stays in sync
      return onChange(e);
    }

    // Normal typing (non-composition): sanitize if ignorePattern is present
    let sanitized = value;
    if (ignorePattern) {
      try {
        const regex = new RegExp(ignorePattern, 'g');
        sanitized = value.replace(regex, '');
      } catch {
        // If ignorePattern is invalid, do not sanitize
      }
    }

    if (controlledValue === undefined) {
      setInnerValue(sanitized);
    }

    if (sanitized !== value) {
      const patchedEvent = {
        ...e,
        target: { ...e.target, value: sanitized },
        currentTarget: { ...e.currentTarget, value: sanitized },
      } as unknown as ChangeEvent<HTMLInputElement>;
      onChange(patchedEvent);
      return;
    }

    onChange(e);
  };

  const handleCompositionStart = () => {
    isComposingRef.current = true;
    setIsComposing(true);
  };

  const handleCompositionEnd = () => {
    isComposingRef.current = false;
    setIsComposing(false);
    // Avoid programmatic dispatch; let the next native input/change update the parent
    setComposingValue(null);
  };

  const handleBlur: React.FocusEventHandler<HTMLInputElement> = e => {
    // Final guard: sanitize on blur as well (avoid IME by running only when not composing)
    if (!isComposingRef.current && ignorePattern) {
      const current = e.currentTarget.value ?? '';
      let sanitized = current;
      try {
        const regex = new RegExp(ignorePattern, 'g');
        sanitized = current.replace(regex, '');
      } catch {
        // If ignorePattern is invalid, do not sanitize
      }
      if (sanitized !== current) {
        if (controlledValue === undefined) {
          setInnerValue(sanitized);
        }
        const patchedEvent = {
          ...e,
          target: { ...e.target, value: sanitized },
          currentTarget: { ...e.currentTarget, value: sanitized },
        } as unknown as ChangeEvent<HTMLInputElement>;
        onChange(patchedEvent);
      }
    }
    props.onBlur?.(e);
  };

  const handleWheel = (e: WheelEvent<HTMLInputElement>) => {
    if (document.activeElement === e.currentTarget && disableScrollChange) {
      e.currentTarget.blur();
    }
    onWheel?.(e);
  };

  const displayValue = isComposing ? composingValue ?? value : value;
  const iconColor: ElementProps['color'] = disabled
    ? 'disabled'
    : isEmpty(displayValue)
      ? 'placeholder'
      : 'primary';

  return (
    <div className={styles.inputWrapper({ disabled, error })}>
      {prefix?.(iconColor)}
      <input
        className={clsx(
          styles.input({ disabled, error }),
          hideStepper && defaultStyles.hideStepper,
          className
        )}
        type={type}
        {...props}
        ref={inputRef}
        disabled={disabled}
        value={displayValue}
        onChange={handleChange}
        onWheel={handleWheel}
        onCompositionStart={e => {
          handleCompositionStart();
          props.onCompositionStart?.(e);
        }}
        onCompositionEnd={e => {
          handleCompositionEnd();
          props.onCompositionEnd?.(e);
        }}
        onBlur={handleBlur}
      />
      {suffix?.(iconColor)}
    </div>
  );
};
